import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TSP } from '../../../core/models';

interface SelectOption<T = string> {
  value: T;
  label: string;
}

@Component({
  selector: 'app-update-portal-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatError,
    MatFormField,
    MatIcon,
    MatSuffix,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    MatSlideToggle,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './update-portal-form.html',
  styleUrl: './update-portal-form.scss',
})
export class UpdatePortalForm implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<UpdatePortalForm>);
  private readonly data = inject<TSP.Internal.Recruiter.Portal.Details>(MAT_DIALOG_DATA);

  public subscriptionOptions: SelectOption<TSP.Internal.Recruiter.Portal.SubscriptionInfo['plan']>[] = [
    { value: 'trial', label: 'Пробний' },
    { value: 'free', label: 'Безкоштовний' },
    { value: 'professional', label: 'Професійний' },
    { value: 'team', label: 'Командний' },
    { value: 'enterprise', label: 'Корпоративний' },
  ];

  public aiCvParserOptions: SelectOption<TSP.Internal.Recruiter.Portal.AiCvParserAccessLevel>[] = [
    { value: TSP.Internal.Recruiter.Portal.AiCvParserAccessLevel.NONE, label: 'Без доступу' },
    { value: TSP.Internal.Recruiter.Portal.AiCvParserAccessLevel.CLASSIC, label: 'Класичний' },
    { value: TSP.Internal.Recruiter.Portal.AiCvParserAccessLevel.FULL, label: 'Повний (OpenAI)' },
  ];

  public form = new FormGroup({
    name: new FormControl<string | null>(null, [Validators.required]),
    jobBoardUrl: new FormControl<string | null>(null),
    aiCvParserAccess: new FormControl<TSP.Internal.Recruiter.Portal.AiCvParserAccessLevel | null>(null, [
      Validators.required,
    ]),
    subscription: new FormControl<TSP.Internal.Recruiter.Portal.SubscriptionInfo['plan'] | null>(null, [
      Validators.required,
    ]),
    endDate: new FormControl<Date | null>(null),
    allowApplyFromPublicWebsite: new FormControl<boolean>(false),
    canAddTeammates: new FormControl<boolean>(true),
    daysToDeadline: new FormControl<number>(30, [Validators.required, Validators.min(1), Validators.max(365)]),
    recruiterCount: new FormControl<number>(999, [Validators.required, Validators.min(1), Validators.max(9999)]),
    searchDuplicatesInAts: new FormControl<boolean>(true),
    searchDuplicatesInConsole: new FormControl<boolean>(true),
    searchDuplicatesInDetails: new FormControl<boolean>(true),
  });

  public minEndDate = new Date();

  public ngOnInit(): void {
    const portal = this.data;
    const f = portal.features;
    this.form.patchValue({
      name: portal.name,
      jobBoardUrl: portal.jobBoardUrl ?? null,
      aiCvParserAccess: portal.features.aiCvParserAccess,
      subscription: portal.subscription.plan.toLowerCase() as TSP.Internal.Recruiter.Portal.SubscriptionInfo['plan'],
      endDate: portal.subscription.endDate ? new Date(portal.subscription.endDate) : null,
      allowApplyFromPublicWebsite: f.allowApplyFromPublicWebsite,
      canAddTeammates: f.canAddTeammates,
      daysToDeadline: f.daysToDeadline,
      recruiterCount: f.recruiterCount,
      searchDuplicatesInAts: f.searchDuplicatesInAts,
      searchDuplicatesInConsole: f.searchDuplicatesInConsole,
      searchDuplicatesInDetails: f.searchDuplicatesInDetails,
    });
  }

  public onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const value: TSP.Internal.Recruiter.Portal.UpdatePayload = {
      id: this.data.id,
      name: raw.name ?? '',
      jobBoardUrl: raw.jobBoardUrl ?? null,
      aiCvParserAccess: raw.aiCvParserAccess ?? TSP.Internal.Recruiter.Portal.AiCvParserAccessLevel.NONE,
      subscription: (raw.subscription ?? 'trial') as TSP.Internal.Recruiter.Portal.SubscriptionInfo['plan'],
      endDate: raw.endDate,
      allowApplyFromPublicWebsite: raw.allowApplyFromPublicWebsite ?? false,
      canAddTeammates: raw.canAddTeammates ?? true,
      daysToDeadline: raw.daysToDeadline ?? 30,
      recruiterCount: raw.recruiterCount ?? 999,
      searchDuplicatesInAts: raw.searchDuplicatesInAts ?? true,
      searchDuplicatesInConsole: raw.searchDuplicatesInConsole ?? true,
      searchDuplicatesInDetails: raw.searchDuplicatesInDetails ?? true,
    };

    this.dialogRef.close(value);
  }

  public onCancel(): void {
    this.dialogRef.close(null);
  }
}
